import { IBankAccount, useBankAccountFieldsConfig } from '@commenda/api';
import {
  Button,
  IconButton,
  Icons,
  Sheet,
  SheetContent,
  SheetHeader,
} from '@commenda/kakashi';
import { useState } from 'react';
import { formatAccountType } from '../../bankAccount/utils';
import { getFieldDef } from '../../form-builder/config/country-bank-utils';
import { useClipboard } from '../../hooks/useClipBoard';

interface FieldRowProps {
  label: string;
  value?: string | null;
}

function FieldRow({ label, value }: FieldRowProps) {
  const [copied, setCopied] = useState(false);
  const { copyToClipboard } = useClipboard();

  if (!value) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await copyToClipboard(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-1">
      <p className="txt-extra-small-medium text-text-secondary">{label}</p>
      <div className="flex items-center justify-between gap-2.5">
        <p className="txt-small-medium text-text-primary break-words">
          {value}
        </p>
        <IconButton
          aria-label="Copy"
          size="sm"
          variant="default"
          onClick={handleCopy}
          className="flex-shrink-0 hover:bg-bg-secondary rounded-xs"
          icon={
            copied ? (
              <Icons.Check className="size-4 text-fg-brand" />
            ) : (
              <Icons.Copy06 className="size-4 text-fg-brand" />
            )
          }
        />
      </div>
    </div>
  );
}

function credentialLabel(
  key: string,
  config: ReturnType<typeof useBankAccountFieldsConfig>['config']
): string | null {
  if (!config) return null;
  return getFieldDef(config, key)?.label ?? null;
}

interface BankAccountViewDrawerProps {
  account: IBankAccount | null;
  isOpen: boolean;
  onClose: () => void;
  // Omit to render the drawer read-only (e.g. on the filing details page).
  onEdit?: (account: IBankAccount) => void;
}

export function BankAccountViewDrawer({
  account,
  isOpen,
  onClose,
  onEdit,
}: BankAccountViewDrawerProps) {
  const { config } = useBankAccountFieldsConfig();
  const creds = account?.credentials as
    | Record<string, string>
    | null
    | undefined;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={false}
    >
      <SheetContent
        side="right"
        hideCloseIcon
        hideOverlay
        className="w-[342px] m-xl h-[calc(100%-2rem)] rounded-lg border border-border-primary shadow-xl p-0 overflow-hidden flex flex-col"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <SheetHeader className="flex flex-row items-center justify-between p-3xl border-b border-border-primary shrink-0">
          <p className="txt-medium-medium text-text-tertiary">Bank Account</p>
          <IconButton
            aria-label="Close"
            size="md"
            variant="default"
            onClick={onClose}
            icon={<Icons.XClose className="size-4 text-text-secondary" />}
          />
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-2">
          {account && (
            <div className="px-3xl flex flex-col gap-3xl">
              <div className="flex flex-col gap-5">
                <p className="txt-extra-large-bold text-text-primary">
                  Details
                </p>

                <div className="flex flex-col gap-4">
                  {/* Top-level fields — always shown if present */}
                  <FieldRow
                    label="Account holder name"
                    value={account.accountHolderName}
                  />
                  <FieldRow label="Bank name" value={account.bankName} />
                  <FieldRow label="Account currency" value={account.currency} />
                  <FieldRow label="Country" value={account.country as string} />

                  {/* Credential fields — from credentials only, labels from JSON config */}
                  {creds &&
                    Object.entries(creds)
                      .filter(([, v]) => !!v)
                      .map(([key, value]) => {
                        const label = credentialLabel(key, config);
                        if (!label) return null;
                        const displayValue =
                          key === 'accountType'
                            ? formatAccountType(value)
                            : value;
                        return (
                          <FieldRow
                            key={key}
                            label={label}
                            value={displayValue}
                          />
                        );
                      })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer — no Edit for Plaid accounts, or when onEdit is omitted */}
        {account && !account.plaidId && onEdit && (
          <div className="border-t border-border-primary p-lg shrink-0">
            <Button
              variant="primary"
              className="w-full justify-center"
              leftIcon={<Icons.Pencil02 className="size-4" />}
              onClick={() => onEdit(account)}
            >
              Edit bank account details
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
